import { useCallback, useRef, useState } from 'react';
import { useAdminAccount } from 'features/hooks/account';
import { exportConversationTraceToFile } from './conversationTraceExport';

export const useConversationTraceExport = ({
  sessionId,
  runId,
  sessionAgentId,
  messages,
  isStreaming,
  activePage,
  agentDisplayName,
}) => {
  const { data: adminAccount } = useAdminAccount();
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  const abortControllerRef = useRef(null);

  const exportConversationTrace = useCallback(async () => {
    if (!adminAccount?.userProfileId) {
      setExportError('User profile ID is required to export the conversation.');
      return false;
    }

    if (abortControllerRef.current) abortControllerRef.current.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setIsExporting(true);
    setExportError(null);
    try {
      await exportConversationTraceToFile({
        sessionId,
        userProfileId: adminAccount.userProfileId,
        messages,
        metadata: {
          runId,
          sessionAgentId,
          activePage,
          agentDisplayName,
          isStreaming,
        },
        signal: controller.signal,
      });
      return true;
    } catch (err) {
      if (err?.name === 'AbortError') return false;
      setExportError(err?.message || 'Failed to export conversation trace.');
      return false;
    } finally {
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
        setIsExporting(false);
      }
    }
  }, [
    adminAccount?.userProfileId,
    sessionId,
    runId,
    sessionAgentId,
    messages,
    isStreaming,
    activePage,
    agentDisplayName,
  ]);

  return {
    exportConversationTrace,
    isExportingConversation: isExporting,
    exportConversationError: exportError,
  };
};
